<script setup lang="ts">
/**
 * `/docs` — public, OpenAI-compatible API documentation (Ollama-styled).
 *
 * Layout:
 *   - desktop (lg+): sticky left section nav + readable content column
 *   - mobile: horizontal scroll of pill tabs above the content
 *
 * Sections (5):
 *   - Quickstart       (static: auth, base URL, endpoints, code samples, tools)
 *   - Models & Pricing (LIVE: /v1/models + /api/models/capabilities + /api/pricing)
 *   - Marketing        (static: UTM params + promo codes)
 *   - White-label      (static prose)
 *   - Compatibility    (static prose)
 *
 * The active section is reflected in `?tab=` so individual sections are
 * shareable as public links. All data sources are public on the router origin,
 * so the page renders without a session.
 */
import { computed, watch } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import QuickstartTab from '../components/docs/QuickstartTab.vue';
import ModelsPricingTab from '../components/docs/ModelsPricingTab.vue';
import MarketingTab from '../components/docs/MarketingTab.vue';
import WhiteLabelTab from '../components/docs/WhiteLabelTab.vue';
import CompatibilityTab from '../components/docs/CompatibilityTab.vue';

type TabKey = 'quickstart' | 'models' | 'marketing' | 'whitelabel' | 'compatibility';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'quickstart', label: 'Quickstart' },
  { key: 'models', label: 'Models & Pricing' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'whitelabel', label: 'White-label' },
  { key: 'compatibility', label: 'Compatibility' },
];

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const validKeys = tabs.map((t) => t.key);

const active = computed<TabKey>(() => {
  const q = route.query.tab;
  return typeof q === 'string' && (validKeys as string[]).includes(q) ? (q as TabKey) : 'quickstart';
});

function select(key: TabKey): void {
  void router.replace({ query: { ...route.query, tab: key } });
}

// Reset scroll when switching section.
watch(active, () => window.scrollTo({ top: 0 }));
</script>

<template>
  <div class="max-w-6xl mx-auto w-full px-6 lg:px-10 py-12">
    <!-- Header -->
    <header class="max-w-2xl mb-10">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute mb-3">Pi documentation</p>
      <h1 class="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Build on Open Gonka.</h1>
      <p class="text-body mt-3">
        An OpenAI-compatible REST API on the Gonka network. Everything here is readable without an
        account; create a key when you’re ready to ship.
      </p>
    </header>

    <div class="lg:grid lg:grid-cols-[200px_1fr] lg:gap-10">
      <!-- Section nav: sticky list on desktop, horizontal pills on mobile -->
      <nav class="lg:sticky lg:top-6 lg:self-start mb-6 lg:mb-0" aria-label="Pi documentation sections">
        <div class="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-1 px-1">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            :aria-current="active === tab.key"
            class="shrink-0 text-left px-3 py-2 rounded-full text-sm font-medium transition whitespace-nowrap"
            :class="
              active === tab.key
                ? 'bg-surface-soft text-ink'
                : 'text-body hover:text-ink hover:bg-surface-soft'
            "
            @click="select(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>
      </nav>

      <!-- Active section body -->
      <section class="min-w-0">
        <QuickstartTab v-if="active === 'quickstart'" />
        <ModelsPricingTab v-else-if="active === 'models'" />
        <MarketingTab v-else-if="active === 'marketing'" />
        <WhiteLabelTab v-else-if="active === 'whitelabel'" />
        <CompatibilityTab v-else-if="active === 'compatibility'" />

        <!-- CTA -->
        <div class="mt-10 card-dark px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h2 class="font-display text-xl font-semibold">Ready to build?</h2>
            <p class="text-on-dark-mute text-sm mt-1">Free tokens on signup. Card or crypto top-ups after that.</p>
          </div>
          <RouterLink v-if="auth.me" to="/keys" class="btn-on-dark shrink-0">Create API key</RouterLink>
          <RouterLink v-else to="/login" class="btn-on-dark shrink-0">Get API key</RouterLink>
        </div>
      </section>
    </div>
  </div>
</template>
